import { createSlice, type PayloadAction, isAnyOf } from "@reduxjs/toolkit";
import { EntitiesEntity } from "app/features/Location/types/YextLocationResponseType";
import {v4 as uuidv4} from 'uuid';
import { selectPickUpType, selectStore } from "app/store/slices/storeSlice";
import {
    CreateUpdateOrderResponse,
    MenuCategory,
    OrderItem,
    OrderModifier,
    TOrderState,
    ApplicableReward,
    AppliedRewards,
    RewardNotApplied,
} from "./tOrderTypes";
import { tOrderApi } from "../services/tOrderApi";
import {
    calculateTotal,
    calculateTotalCount,
    checkIfOrderItemExists,
    getDefaultInitialState,
    getDefaultPickupType,
    getProcessedStoreInformation,
    constructItemWithUpdatedQty,
    getItemUniqueIdByItem,
    shouldPreferIncomingCartItem,
} from "./orderSliceUtil";

function getPreferredCartItemForMerge(
    existingCartItem: OrderItem,
    incomingCartItem: OrderItem,
    existingItemUniqueId: string,
) {
    const preferredCartItem = shouldPreferIncomingCartItem(existingCartItem, incomingCartItem)
        ? incomingCartItem
        : existingCartItem;

    return {
        ...preferredCartItem,
        itemUniqueId: existingItemUniqueId,
    };
}

const getLineTotal = (item: OrderItem): number => item.quantity * (item.price || 0);

const refreshOrderTotals = (state: TOrderState) => {
    state.orderLines = Object.values(state.lineItemMap);
    state.total = calculateTotal(state.orderLines);
    state.totalCartItems = calculateTotalCount(state.orderLines);
};

export const tOrderSlice = createSlice({
    name: "tOrders",
    initialState: getDefaultInitialState(),
    extraReducers: (builder) => {
        builder.addCase(selectPickUpType, (state, { payload: sessionPickupKey }) => {
            const {
                pickupType,
                diningOption,
            } = getDefaultPickupType(state.pickupTypeMap, sessionPickupKey);
            state.pickupType = pickupType;
            state.diningOption = diningOption;
        });
        builder.addCase(selectStore, (state, { payload }) => {
            const storeEntity = payload as unknown as EntitiesEntity;
            const { storeId, pickupTypeMap } = getProcessedStoreInformation(storeEntity);
            state.storeId = storeId;
            state.pickupTypeMap = pickupTypeMap;
        });
        builder.addMatcher(tOrderApi.endpoints.getMenu.matchFulfilled, (state, action) => {
            state.menu = action.payload;
        });
        builder.addMatcher(isAnyOf(
                tOrderApi.endpoints.createOrder.matchFulfilled,
                tOrderApi.endpoints.updateOrder.matchFulfilled,
            ), (state, action) => {
            const payload = action.payload as unknown as CreateUpdateOrderResponse["data"];
            const {
                summary,
                serviceCharges,
                orderTransactionId,
                items,
            } = payload;
            state.summary = summary;
            state.serviceCharges = serviceCharges;
            state.orderId = orderTransactionId;
            state.appliedReward = undefined;
            state.applicableReward = undefined;
            state.rewardNotApplied = undefined;
            const lineItemsMap:Record<string, OrderItem> = {};
            items.reduce((itemAccumulator, item) => {
                const itemUniqId = getItemUniqueIdByItem(item, state.orderLines);
                if (itemUniqId && state.lineItemMap[itemUniqId]) {
                    const {
                        modifiers: existingItemModifiers,
                        ...existingItem
                    } = state.lineItemMap[itemUniqId];
                    const newItemModifierMap:Record<string, OrderModifier> = {};
                    item.modifiers.reduce((modifierAccumulator, existingItemModifier) => {
                        const {modifierGroupId, optionId} = existingItemModifier;
                        modifierAccumulator[`${modifierGroupId}${optionId}`] = existingItemModifier;
                        return modifierAccumulator;
                    }, newItemModifierMap);
                    const updatedModifier = existingItemModifiers.map((currentItemModifier) => {
                        const {modifierGroupId, optionId} = currentItemModifier;
                        const modifierGroupKey = `${modifierGroupId}${optionId}`;
                        const modifierPrice = newItemModifierMap[modifierGroupKey]
                                        ? (newItemModifierMap[modifierGroupKey].price || 0) : 0;
                        return {
                            ...currentItemModifier,
                            order: { price: modifierPrice },
                        };
                    });
                    const {total = 0, price = 0} = item;
                    const order = {total: total.toFixed(2), price};
                    itemAccumulator[itemUniqId] = {
                        ...existingItem,
                        order,
                        modifiers: updatedModifier,
                    };
                }
                return itemAccumulator;
            }, lineItemsMap);
            state.lineItemMap = lineItemsMap;
            state.orderLines = Object.values(lineItemsMap);
            if (payload.appliedReward) {
                state.appliedReward = payload.appliedReward;
            }
            if (payload.applicableRewards && payload.applicableRewards.length > 0) {
                const [applicableReward] = payload.applicableRewards;
                state.applicableReward = applicableReward;
            }
            if (payload.rewardNotApplied && payload.rewardNotApplied.length > 0) {
                const [rewardNotApplied] = payload.rewardNotApplied;
                state.rewardNotApplied = rewardNotApplied;
            }
        });
        builder.addMatcher(tOrderApi.endpoints.loyaltyRewards.matchFulfilled, (state, action) => {
            state.wallets = action.payload.wallets || [];
        });
    },
    reducers: {
        updateApplicableReward: (state, action: PayloadAction<ApplicableReward | null>) => {
            state.applicableReward = action.payload || undefined;
        },
        updateAppliedReward: (state, action: PayloadAction<AppliedRewards | null>) => {
            state.appliedReward = action.payload || undefined;
        },
        updateErroredReward: (state, action: PayloadAction<RewardNotApplied | null>) => {
            state.rewardNotApplied = action.payload || undefined;
        },
        updateSelectedCategoryName: (state, action: PayloadAction<MenuCategory[`categoryName`]>) => {
            state.selectedCategoryName = action.payload;
        },
        updateRewardSelectedOnPage: (state, action: PayloadAction<boolean>) => {
            state.hasRewardSelectedFromPage = action.payload;
        },
        updateShellRewardInitialized: (state, action: PayloadAction<boolean>) => {
            state.isShellRewardInitialized = action.payload;
        },
        updateShellAppInitialized: (state, action: PayloadAction<boolean>) => {
            state.isShellAppInitialized = action.payload;
        },
        updateCart: (state, action: PayloadAction<OrderItem>) => {
            const item = action.payload;
            // Matching an existing item means same base item plus same selected customizations.
            const itemUniqueIdOfExistingItem = checkIfOrderItemExists(item, state.orderLines);
            if (!item.itemUniqueId) {
                if (itemUniqueIdOfExistingItem) {
                    // New add of an equivalent item increments the existing cart line.
                    const existingCartItem = state.lineItemMap[itemUniqueIdOfExistingItem];
                    const updatedQuantity = existingCartItem.quantity + item.quantity;
                    const itemToIncrement = getPreferredCartItemForMerge(
                        existingCartItem,
                        item,
                        itemUniqueIdOfExistingItem,
                    );

                    state.lineItemMap[itemUniqueIdOfExistingItem] = constructItemWithUpdatedQty(
                        itemToIncrement,
                        updatedQuantity,
                    );
                } else {
                    // Brand-new cart lines get a local id so future edits can target them.
                    const cartItemUniqueId = uuidv4();
                    state.lineItemMap[cartItemUniqueId] = {
                        ...item,
                        itemUniqueId: cartItemUniqueId,
                        total: getLineTotal(item),
                    };
                }
            } else if (
                !itemUniqueIdOfExistingItem
                || itemUniqueIdOfExistingItem === item.itemUniqueId
            ) {
                state.lineItemMap[item.itemUniqueId] = {
                    ...item,
                    total: getLineTotal(item),
                };
            } else {
                // Edited item now matches another line, so merge quantities and
                // remove the old line.
                delete state.lineItemMap[item.itemUniqueId];

                const existingCartItem = state.lineItemMap[itemUniqueIdOfExistingItem];
                const updatedQuantity = existingCartItem.quantity + item.quantity;
                const itemToIncrement = getPreferredCartItemForMerge(
                    existingCartItem,
                    item,
                    itemUniqueIdOfExistingItem,
                );

                state.lineItemMap[itemUniqueIdOfExistingItem] = constructItemWithUpdatedQty(
                    itemToIncrement,
                    updatedQuantity,
                );
            }
            refreshOrderTotals(state);
        },
        deleteLineItem(state, { payload: { itemUniqueId = "" } }: PayloadAction<OrderItem>) {
            if (state.lineItemMap[itemUniqueId]) {
                delete state.lineItemMap[itemUniqueId];
            }
            refreshOrderTotals(state);
        },
        updateQuantity(
            state,
            { payload }: PayloadAction<{ itemUniqueId: string; quantity: number }>,
        ) {
            const { itemUniqueId, quantity } = payload;
            if (state.lineItemMap[itemUniqueId]) {
                const currentItem = state.lineItemMap[itemUniqueId];

                state.lineItemMap[itemUniqueId] = constructItemWithUpdatedQty(
                    currentItem,
                    quantity,
                );
            }
            refreshOrderTotals(state);
        },
        updatePostOrderUpdate(state, { payload }: PayloadAction<Partial<TOrderState>>) {
            if (payload.summary) {
                state.summary = payload.summary;
            }
            if (payload.serviceCharges) {
                state.serviceCharges = payload.serviceCharges;
            }
            if (state.lineItemMap && payload.lineItemMap) {
                state.lineItemMap = payload.lineItemMap;
            }
            if (payload.orderId) {
                state.orderId = payload.orderId;
            }
        },
        setCartItemSelectedForEdit: (state, action: PayloadAction<OrderItem | null>) => {
            state.cartItemSelectedForEdit = action.payload;
        },
        resetTOrder: (state) => ({
                ...getDefaultInitialState(),
                orderLines: [],
                menu: state.menu,
                pickupType: "",
                diningOption: "",
            }),
    },
});

export default tOrderSlice.reducer;

export const {
    updateApplicableReward,
    updateAppliedReward,
    updateErroredReward,
    updateSelectedCategoryName,
    updateCart,
    setCartItemSelectedForEdit,
    deleteLineItem,
    updatePostOrderUpdate,
    updateQuantity,
    updateShellRewardInitialized,
    updateShellAppInitialized,
    updateRewardSelectedOnPage,
    resetTOrder,
} = tOrderSlice.actions;
